/**
 * Sistema → Etiquetas (#MT-6405654 / #MT-4888116).
 * Lista das etiquetas que o SIAL gera e editor ZPL por empresa, com prévia do Labelary
 * (dados de exemplo) e lista de variáveis. Sem modelo salvo, a impressão usa o padrão.
 */
import api from '../api.js';
import msgbox, { notify, ask } from '../../versa/msgbox.js';

const TITLE = 'Etiquetas';
const LABELARY_URL = 'https://api.labelary.com/v1/printers';
const PREVIEW_DELAY_MS = 900;
const MM_PER_INCH = 25.4;
const LOGO_MAX_BYTES = 64000;
const LOGO_DEFAULT_W = 120;
const LOGO_DEFAULT_H = 46;
const LOGO_DEFAULT_CUT = 160;
const GFA_RE = /^\^GFA,(\d+),(\d+),(\d+),([0-9A-F]+)$/;

const state = {
	companies: [],
	devices: [],
	companyId: '',
	list: [],
	current: null,
	savedZpl: '',
	dirty: false,
	previewTimer: null,
	previewSeq: 0,
	imageUrl: null,
	logo: null,
	logoImage: null,
	logoDraft: null,
};

/**
 * Escapa texto para HTML.
 * @param {unknown} value
 * @returns {string}
 */
const esc = (value) => String(value ?? '')
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;')
	.replace(/'/g, '&#39;');

const $ = (sel, ctx = document) => ctx.querySelector(sel);

/**
 * Data ISO → dd/mm/aaaa hh:mm (horário local).
 * @param {string|null} iso
 * @returns {string}
 */
const formatDateTime = (iso) =>
{
	if (!iso)
		return '';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime()))
		return '';
	return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Texto da coluna Modelo.
 * @param {object} item
 * @returns {string}
 */
const modelStatus = (item) =>
{
	if (!item.is_custom)
		return 'Padrão do SIAL';
	const who = item.updated_by_name ? ` por ${item.updated_by_name}` : '';
	const when = item.updated_at ? ` em ${formatDateTime(item.updated_at)}` : '';
	return `Personalizado${who}${when}`;
};

/**
 * Lê a mensagem de erro da API sem abrir o aviso padrão.
 * @param {Response} response
 * @returns {Promise<string>}
 */
const responseError = async (response) =>
{
	try
	{
		const text = await response.text();
		return text || `${response.status} ${response.statusText}`;
	}
	catch (e)
	{
		return `${response.status} ${response.statusText}`;
	}
};

/**
 * Converte a imagem em ^GFA monocromático (1 = ponto preto), cabendo em maxW × maxH pontos.
 * Fundo transparente vira branco.
 * @param {CanvasImageSource & { width: number, height: number }} image
 * @param {number} maxW
 * @param {number} maxH
 * @param {number} cut Brilho abaixo do qual o ponto sai preto (0–255)
 * @returns {{ gfa: string, width: number, height: number, bits: Uint8Array }}
 */
const imageToGfa = (image, maxW, maxH, cut) =>
{
	const scale = Math.min(maxW / image.width, maxH / image.height);
	const width = Math.max(1, Math.round(image.width * scale));
	const height = Math.max(1, Math.round(image.height * scale));
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext('2d');
	ctx.fillStyle = '#fff';
	ctx.fillRect(0, 0, width, height);
	ctx.drawImage(image, 0, 0, width, height);
	const { data } = ctx.getImageData(0, 0, width, height);
	const perRow = Math.ceil(width / 8);
	const bits = new Uint8Array(perRow * height);
	for (let y = 0; y < height; y++)
	{
		for (let x = 0; x < width; x++)
		{
			const i = (y * width + x) * 4;
			const light = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
			if (light < cut)
				bits[y * perRow + (x >> 3)] |= 0x80 >> (x & 7);
		}
	}
	const hex = Array.from(bits, (b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
	return { gfa: `^GFA,${bits.length},${bits.length},${perRow},${hex}`, width: perRow * 8, height, bits };
};

/**
 * Desenha um ^GFA no canvas, ampliado para enxergar.
 * @param {HTMLCanvasElement} canvas
 * @param {string|null} gfa
 * @param {number} [zoom]
 * @returns {boolean} false se não há imagem válida
 */
const drawGfa = (canvas, gfa, zoom = 2) =>
{
	const match = GFA_RE.exec(gfa || '');
	if (!match)
		return false;
	const total = Number(match[1]);
	const perRow = Number(match[3]);
	const hex = match[4];
	const width = perRow * 8;
	const height = total / perRow;
	canvas.width = width * zoom;
	canvas.height = height * zoom;
	const ctx = canvas.getContext('2d');
	ctx.fillStyle = '#fff';
	ctx.fillRect(0, 0, canvas.width, canvas.height);
	ctx.fillStyle = '#000';
	for (let y = 0; y < height; y++)
	{
		for (let col = 0; col < perRow; col++)
		{
			const byte = parseInt(hex.substr((y * perRow + col) * 2, 2), 16);
			if (!byte)
				continue;
			for (let bit = 0; bit < 8; bit++)
			{
				if (byte & (0x80 >> bit))
					ctx.fillRect((col * 8 + bit) * zoom, y * zoom, zoom, zoom);
			}
		}
	}
	return true;
};

/**
 * Lê o arquivo de imagem escolhido.
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
const loadImageFile = (file) => new Promise((resolve, reject) =>
{
	const url = URL.createObjectURL(file);
	const img = new Image();
	img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
	img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Não foi possível ler a imagem. Use PNG, JPG, GIF, BMP ou WEBP.')); };
	img.src = url;
});

/**
 * Quadro da logo do cliente (abaixo da lista).
 * @returns {string}
 */
const logoHtml = () =>
{
	const logo = state.logo;
	const current = logo?.gfa
		? `${esc(logo.width_dots)} × ${esc(logo.height_dots)} pontos${logo.updated_by_name ? ` · enviada por ${esc(logo.updated_by_name)}` : ''}${logo.updated_at ? ` em ${esc(formatDateTime(logo.updated_at))}` : ''}`
		: (logo?.is_default ? 'Logo padrão. Nenhuma enviada por esta empresa.' : 'Nenhuma logo enviada.');
	return `
		<div class="lt-logo">
			<strong>Logo do cliente</strong>
			<p class="lt-note">Vale para todas as etiquetas desta empresa. No modelo, use <code>{logo_cliente}</code> depois da posição: ex. <code>^FO34,11{logo_cliente}</code>. Sem logo enviada, sai a logo padrão.</p>
			<div class="lt-logo-row">
				<div class="lt-logo-box">
					<span class="lt-note">Em uso</span>
					<canvas id="lt-logo-current" width="1" height="1"${logo?.gfa ? '' : ' hidden'}></canvas>
					<span class="lt-note">${current}</span>
				</div>
				<div class="lt-logo-box">
					<div class="lt-logo-fields">
						<label title="PNG, JPG, GIF, BMP ou WEBP. Fundo transparente vira branco.">Imagem<input id="lt-logo-file" type="file" accept="image/png,image/jpeg,image/gif,image/bmp,image/webp"></label>
						<label title="Largura máxima em pontos (8 pontos = 1 mm em 203 dpi)">Largura máx.<input id="lt-logo-w" type="number" min="8" max="800" step="1" value="${LOGO_DEFAULT_W}"></label>
						<label title="Altura máxima em pontos">Altura máx.<input id="lt-logo-h" type="number" min="8" max="640" step="1" value="${LOGO_DEFAULT_H}"></label>
						<label title="Mais à direita: mais pontos pretos">Corte claro/escuro<input id="lt-logo-cut" type="range" min="16" max="240" step="1" value="${LOGO_DEFAULT_CUT}"></label>
					</div>
					<span class="lt-note" id="lt-logo-info">Escolha a imagem para ver como ela sai na impressora (preto e branco).</span>
					<canvas id="lt-logo-draft" width="1" height="1" hidden></canvas>
					<div class="lt-toolbar">
						<button type="button" id="lt-logo-save" class="button lt-btn-primary" disabled>Salvar logo</button>
						${logo?.gfa && !logo.is_default ? '<button type="button" id="lt-logo-remove" class="button lt-btn-danger">Remover logo</button>' : ''}
					</div>
				</div>
			</div>
		</div>`;
};

/**
 * Recalcula a logo escolhida com o tamanho e o corte atuais.
 * @param {HTMLElement} container
 */
const refreshLogoDraft = (container) =>
{
	const canvas = $('#lt-logo-draft', container);
	const info = $('#lt-logo-info', container);
	const saveBtn = $('#lt-logo-save', container);
	if (!canvas || !state.logoImage)
		return;
	const maxW = Math.max(8, Math.min(800, Number($('#lt-logo-w', container).value) || LOGO_DEFAULT_W));
	const maxH = Math.max(8, Math.min(640, Number($('#lt-logo-h', container).value) || LOGO_DEFAULT_H));
	const cut = Number($('#lt-logo-cut', container).value) || LOGO_DEFAULT_CUT;
	const draft = imageToGfa(state.logoImage, maxW, maxH, cut);
	drawGfa(canvas, draft.gfa);
	canvas.hidden = false;
	const tooBig = draft.bits.length > LOGO_MAX_BYTES;
	state.logoDraft = tooBig ? null : draft;
	saveBtn.disabled = tooBig;
	info.className = tooBig ? 'lt-note lt-error' : 'lt-note';
	info.textContent = tooBig
		? 'A logo ficou grande demais para a etiqueta. Diminua a largura ou a altura.'
		: `Como sai na impressora: ${draft.width} × ${draft.height} pontos (≈ ${Math.round(draft.width / 8)} × ${Math.round(draft.height / 8)} mm em 203 dpi).`;
};

const styles = `
	#label-templates-wrap { padding: var(--space-lg); background-color: var(--color-secondary); min-height: 100%; box-sizing: border-box; }
	#label-templates { max-width: 96rem; margin: 0 auto; }
	#label-templates .lt-head { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
	#label-templates .lt-title-row { display: flex; align-items: center; gap: 0.45rem; flex: 1; }
	#label-templates .lt-head .title { margin: 0; }
	#label-templates .lt-help { display: inline-flex; align-items: center; justify-content: center; width: 1.35rem; height: 1.35rem; padding: 0; border: 1px solid #9ca3af; border-radius: 999px; background: #f3f4f6; color: #374151; font-size: 0.85rem; font-weight: 700; line-height: 1; cursor: pointer; }
	#label-templates .lt-help:hover { background: #e5e7eb; }
	#label-templates .lt-help:focus { outline: 2px solid #2563eb; outline-offset: 2px; }
	.lt-help-body { max-height: 65vh; overflow-y: auto; text-align: left; }
	.lt-help-body h4 { margin: 0.85rem 0 0.25rem 0; font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #1f2937; }
	.lt-help-body h4:first-child { margin-top: 0; }
	.lt-help-body p { margin: 0; font-size: 0.875rem; line-height: 1.45; color: #374151; }
	.lt-help-body code { font-family: Consolas, 'Courier New', monospace; font-size: 0.8rem; }
	#label-templates #lt-company { width: 14rem; }
	#label-templates table.lt-list { width: 100%; border-collapse: collapse; margin-top: var(--space-md); background: #fff; }
	#label-templates table.lt-list th, #label-templates table.lt-list td { border: 1px solid var(--color-border); padding: 0.4rem 0.6rem; text-align: left; font-size: 0.85rem; }
	#label-templates table.lt-list tbody tr { cursor: pointer; }
	#label-templates table.lt-list tbody tr:hover { background: #f1f5f9; }
	#label-templates .lt-custom { color: #0a6b0a; font-weight: 600; }
	#label-templates .lt-editor { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: var(--space-md); margin-top: var(--space-md); }
	#label-templates .lt-pane { background: #fff; border: 1px solid var(--color-border); border-radius: 4px; padding: var(--space-sm); min-width: 0; }
	#label-templates #lt-zpl { width: 100%; height: 30rem; font-family: Consolas, 'Courier New', monospace; font-size: 0.8rem; line-height: 1.35; box-sizing: border-box; white-space: pre; resize: vertical; }
	#label-templates .lt-toolbar { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: var(--space-sm); }
	#label-templates .lt-paper { display: flex; gap: 0.75rem; align-items: flex-end; flex-wrap: wrap; }
	#label-templates .lt-paper input { width: 4.5rem; }
	#label-templates .lt-paper select { width: 6.5rem; }
	#label-templates .lt-preview { margin-top: var(--space-sm); min-height: 18rem; display: flex; align-items: center; justify-content: center; background: #e5e7eb; border-radius: 4px; padding: var(--space-sm); }
	#label-templates .lt-preview img { width: auto; height: auto; max-width: 100%; max-height: 36rem; cursor: default; filter: none; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.25); }
	#label-templates .lt-msg { font-size: 0.85rem; margin-top: var(--space-sm); }
	#label-templates .lt-error { color: var(--color-error); white-space: pre-wrap; }
	#label-templates .lt-note { color: #4b5563; font-size: 0.8rem; }
	#label-templates .lt-vars { margin-top: var(--space-md); max-height: 22rem; overflow-y: auto; }
	#label-templates .lt-vars table { width: 100%; border-collapse: collapse; }
	#label-templates .lt-vars th, #label-templates .lt-vars td { border-bottom: 1px solid var(--color-border); padding: 0.3rem 0.4rem; text-align: left; font-size: 0.8rem; vertical-align: top; }
	#label-templates .lt-var-btn { font-family: Consolas, 'Courier New', monospace; font-size: 0.8rem; cursor: pointer; background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 3px; padding: 0.1rem 0.35rem; white-space: nowrap; }
	#label-templates .lt-var-btn:hover { background: #e0e7ff; }
	#label-templates .lt-sample { color: #4b5563; word-break: break-all; }
	#label-templates .lt-print { display: flex; gap: 0.75rem; align-items: flex-end; flex-wrap: wrap; margin-top: var(--space-md); }
	#label-templates #lt-device { width: 18rem; }
	#label-templates .lt-btn-primary { background: linear-gradient(180deg, #4ade80 0%, #22c55e 100%); color: #fff; border: 1px solid #15803d; font-weight: 600; }
	#label-templates .lt-btn-muted { background: #f8fafc; color: #334155; border: 1px solid var(--color-border); }
	#label-templates .lt-btn-danger { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
	#label-templates .lt-logo { margin-top: var(--space-lg); background: #fff; border: 1px solid var(--color-border); border-radius: 4px; padding: var(--space-sm) var(--space-md); }
	#label-templates .lt-logo-row { display: flex; gap: var(--space-md); align-items: flex-start; flex-wrap: wrap; margin-top: var(--space-sm); }
	#label-templates .lt-logo-box { display: flex; flex-direction: column; gap: 0.3rem; }
	#label-templates .lt-logo-box canvas { background: #fff; border: 1px dashed #94a3b8; image-rendering: pixelated; max-width: 30rem; }
	#label-templates .lt-logo-fields { display: flex; gap: 0.75rem; align-items: flex-end; flex-wrap: wrap; }
	#label-templates .lt-logo-fields input[type="number"] { width: 5rem; }
	#label-templates .lt-logo-fields input[type="range"] { width: 8rem; }
	#label-templates #lt-logo-file { width: 18rem; }
	@media (max-width: 70rem) { #label-templates .lt-editor { grid-template-columns: 1fr; } }
`;

const HELP_HTML = `
<div class="lt-help-body">
<h4>O que esta tela faz</h4>
<p>O texto da esquerda é o modelo da etiqueta. O que você salvar é o que o SIAL imprime para esta empresa. Sem modelo salvo, sai o padrão.</p>
<h4>Como alterar</h4>
<p>Edite o texto. A prévia à direita atualiza sozinha, com dados de exemplo (precisa de internet). Largura, altura e resolução mudam só o desenho da prévia.</p>
<h4>Variáveis</h4>
<p>Clique numa variável da lista para colocá-la onde está o cursor. O nome fica entre chaves, por exemplo <code>{cliente}</code>. <code>{saida}</code> é o número da saída (não é o pedido nem a fatura). Na impressão o SIAL troca pelo dado real daquela saída, lote ou posição. Use só os nomes da lista: um nome que não existe é recusado ao salvar.</p>
<h4>Posição na etiqueta</h4>
<p><code>^FO20,40</code> coloca o que vem depois a 20 pontos da esquerda e 40 do topo. Em 203 dpi, 8 pontos são 1 mm. O que você escreve entre <code>^FD</code> e <code>^FS</code> sai igual em todas as vias. Os sinais <code>^</code> e <code>~</code> dentro de um texto viram espaço.</p>
<h4>Logo do cliente</h4>
<p>Volte à lista e envie a imagem em <strong>Logo do cliente</strong>. No modelo, coloque <code>{logo_cliente}</code> logo depois da posição, por exemplo <code>^FO34,11{logo_cliente}</code>. Sem logo enviada, sai a logo padrão.</p>
<h4>Salvar</h4>
<p><strong>Salvar</strong> grava o modelo desta empresa. A próxima impressão já usa o novo. <strong>Descartar alterações</strong> volta ao que estava gravado. <strong>Carregar padrão no editor</strong> só coloca o padrão no texto; só grava quando você salva. <strong>Voltar ao padrão</strong> apaga o modelo da empresa.</p>
<h4>Início e fim</h4>
<p>A etiqueta começa com <code>^XA</code> e termina com <code>^XZ</code>. Não tire esses dois. A etiqueta de lote não leva <code>^XA</code> nem <code>^XZ</code>: o SIAL imprime duas por linha no rolo.</p>
<h4>Imprimir teste</h4>
<p>Escolha a impressora e clique em <strong>Imprimir teste</strong>. Sai uma via com os dados de exemplo, não com os dados de uma saída real.</p>
</div>`;

/**
 * Abre a explicação de como alterar o modelo.
 */
const openHelp = () =>
{
	let mb;
	mb = msgbox({
		title: 'Ajuda — como modificar a etiqueta',
		text: HELP_HTML,
		buttons: [{ label: 'Fechar', cls: 'bn_Enter' }],
		callback: () => mb.destroy(),
	});
};

/**
 * Cabeçalho com a escolha da empresa.
 * O ? fica ao lado do título do editor (Etiquetas — nome da etiqueta).
 * @param {string} [subtitle]
 * @returns {string}
 */
const headHtml = (subtitle) => `
	<div class="lt-head">
		<div class="lt-title-row">
			<div class="title">${esc(TITLE)}${subtitle ? ` — ${esc(subtitle)}` : ''}</div>
			${subtitle ? '<button type="button" class="lt-help" id="lt-help" title="Ajuda: como modificar a etiqueta" aria-label="Ajuda — como modificar a etiqueta">?</button>' : ''}
		</div>
		<label title="Cada empresa tem o seu modelo. Sem modelo, vale o padrão do SIAL.">Empresa<sup>*</sup>
			<select id="lt-company">
				${state.companies.map((c) => `<option value="${esc(c.id)}"${c.id === state.companyId ? ' selected' : ''}>${esc(c.nick || c.name || c.id)}</option>`).join('')}
			</select>
		</label>
	</div>`;

/**
 * Lista das etiquetas da empresa escolhida.
 * @param {HTMLElement} container
 */
const renderList = (container) =>
{
	state.current = null;
	state.dirty = false;
	const rows = state.list.map((item) => `
		<tr data-key="${esc(item.key)}" title="Abrir o editor">
			<td>${esc(item.name)}</td>
			<td>${esc(item.used_in)}</td>
			<td>${esc(item.width_mm)} × ${esc(item.height_mm)} mm</td>
			<td class="${item.is_custom ? 'lt-custom' : ''}">${esc(modelStatus(item))}</td>
		</tr>`).join('');
	container.innerHTML = `
		<div class="form">
			${headHtml('')}
			<p class="lt-note mts">Clique na etiqueta para editar. A impressão usa o modelo da empresa; sem modelo, usa o padrão do SIAL.</p>
			<table class="lt-list">
				<thead><tr><th>Etiqueta</th><th>Onde é usada</th><th>Papel</th><th>Modelo</th></tr></thead>
				<tbody>${rows || '<tr><td colspan="4">Nenhuma etiqueta.</td></tr>'}</tbody>
			</table>
			${logoHtml()}
		</div>`;
	state.logoImage = null;
	state.logoDraft = null;
	const currentCanvas = $('#lt-logo-current', container);
	if (currentCanvas && state.logo?.gfa)
		drawGfa(currentCanvas, state.logo.gfa);
};

/**
 * Editor ZPL + prévia + variáveis.
 * @param {HTMLElement} container
 */
const renderEditor = (container) =>
{
	const rec = state.current;
	const vars = (rec.variables || []).map((v) => `
		<tr>
			<td><button type="button" class="lt-var-btn" data-var="${esc(v.name)}" title="Inserir no cursor">{${esc(v.name)}}</button></td>
			<td>${esc(v.description)}</td>
			<td class="lt-sample">${esc(v.sample)}</td>
		</tr>`).join('');
	const printers = state.devices
		.filter((d) => d.company_id === state.companyId)
		.map((d) => `<option value="${esc(d.id)}">${esc(d.name)} (${esc(d.address)}:${esc(d.port)})</option>`)
		.join('');
	container.innerHTML = `
		<div class="form">
			${headHtml(rec.name)}
			<p class="lt-note mts">${esc(rec.used_in)} · ${modelStatus(rec) === 'Padrão do SIAL' ? 'usando o padrão do SIAL' : esc(modelStatus(rec))}</p>
			${rec.fragment_note ? `<p class="lt-note">${esc(rec.fragment_note)}</p>` : ''}
			<div class="lt-editor">
				<div class="lt-pane">
					<label for="lt-zpl"><strong>Modelo ZPL</strong></label>
					<textarea id="lt-zpl" spellcheck="false" autocomplete="off"></textarea>
					<div class="lt-toolbar">
						<button type="button" id="lt-save" class="button lt-btn-primary">Salvar</button>
						<button type="button" id="lt-discard" class="button lt-btn-muted">Descartar alterações</button>
						<button type="button" id="lt-load-default" class="button lt-btn-muted" title="Coloca o modelo padrão no editor (só grava ao salvar)">Carregar padrão no editor</button>
						${rec.is_custom ? '<button type="button" id="lt-reset" class="button lt-btn-danger" title="Apaga o modelo da empresa">Voltar ao padrão</button>' : ''}
						<button type="button" id="lt-back" class="button lt-btn-muted">Voltar à lista</button>
					</div>
					<div class="lt-vars">
						<strong>Variáveis</strong> <span class="lt-note">— clique para inserir no cursor. O SIAL troca pelo valor de cada etiqueta.</span>
						<table>
							<thead><tr><th>Variável</th><th>O que é</th><th>Exemplo na prévia</th></tr></thead>
							<tbody>${vars}</tbody>
						</table>
					</div>
				</div>
				<div class="lt-pane">
					<div class="lt-paper">
						<strong>Prévia</strong>
						<label title="Largura do papel para a prévia">Largura (mm)<input id="lt-w" type="number" min="10" max="300" step="1"></label>
						<label title="Altura do papel para a prévia">Altura (mm)<input id="lt-h" type="number" min="10" max="300" step="1"></label>
						<label title="Resolução da impressora">Resolução
							<select id="lt-dpmm">
								<option value="6">152 dpi</option>
								<option value="8">203 dpi</option>
								<option value="12">300 dpi</option>
								<option value="24">600 dpi</option>
							</select>
						</label>
						<button type="button" id="lt-refresh" class="button lt-btn-muted">Atualizar prévia</button>
					</div>
					<div class="lt-preview" id="lt-preview"><span class="lt-note">Gerando prévia…</span></div>
					<div class="lt-msg" id="lt-msg"></div>
					<p class="lt-note">A prévia usa os dados de exemplo e é desenhada pelo labelary.com (precisa de internet). A impressão real usa os dados da saída, do lote ou da posição.</p>
					<div class="lt-print">
						<label title="Impressora cadastrada em Dispositivos para esta empresa">Impressora
							<select id="lt-device">
								<option value="">Escolha…</option>
								${printers}
							</select>
						</label>
						<label title="SIAL na AWS: envie o ZPL ao agente local que fala com a impressora da rede interna">Usar agente local
							<input id="lt-agent" type="checkbox">
						</label>
						<button type="button" id="lt-test-print" class="button lt-btn-muted">Imprimir teste</button>
					</div>
				</div>
			</div>
		</div>`;
	$('#lt-zpl', container).value = state.savedZpl;
	$('#lt-w', container).value = rec.preview_width_mm || rec.width_mm;
	$('#lt-h', container).value = rec.height_mm;
	$('#lt-dpmm', container).value = String(rec.dpmm || 8);
	schedulePreview(container, 0);
};

/**
 * Atualiza o texto de mensagem do editor.
 * @param {HTMLElement} container
 * @param {string} text
 * @param {boolean} [isError]
 */
const setMessage = (container, text, isError = false) =>
{
	const el = $('#lt-msg', container);
	if (!el)
		return;
	el.className = isError ? 'lt-msg lt-error' : 'lt-msg';
	el.textContent = text || '';
};

/**
 * Agenda a prévia depois que o usuário para de digitar.
 * @param {HTMLElement} container
 * @param {number} [delay]
 */
const schedulePreview = (container, delay = PREVIEW_DELAY_MS) =>
{
	clearTimeout(state.previewTimer);
	state.previewTimer = setTimeout(() => { refreshPreview(container); }, delay);
};

/**
 * Pede o ZPL com os dados de exemplo e desenha no Labelary.
 * @param {HTMLElement} container
 */
const refreshPreview = async (container) =>
{
	const rec = state.current;
	const box = $('#lt-preview', container);
	if (!rec || !box)
		return;
	const seq = ++state.previewSeq;
	const zpl = $('#lt-zpl', container).value;
	try
	{
		const response = await api.preview_label_template(state.companyId, rec.key, { zpl });
		if (seq !== state.previewSeq)
			return;
		if (!response.ok)
		{
			setMessage(container, await responseError(response), true);
			return;
		}
		const data = await response.json();
		const width = Number($('#lt-w', container).value) || data.width_mm;
		const height = Number($('#lt-h', container).value) || data.height_mm;
		const dpmm = Number($('#lt-dpmm', container).value) || data.dpmm || 8;
		const inches = (mm) => (Math.max(10, Math.min(300, mm)) / MM_PER_INCH).toFixed(2);
		const form = new FormData();
		form.append('file', new Blob([data.zpl], { type: 'text/plain' }), 'etiqueta.zpl');
		let image;
		try
		{
			image = await fetch(`${LABELARY_URL}/${dpmm}dpmm/labels/${inches(width)}x${inches(height)}/0/`, {
				method: 'POST',
				headers: { Accept: 'image/png' },
				body: form,
			});
		}
		catch (e)
		{
			if (seq === state.previewSeq)
				setMessage(container, 'Sem acesso ao labelary.com. A prévia precisa de internet; o modelo pode ser salvo e testado na impressora.', true);
			return;
		}
		if (seq !== state.previewSeq)
			return;
		if (!image.ok)
		{
			setMessage(container, `Labelary não desenhou a etiqueta: ${await responseError(image)}`, true);
			return;
		}
		const blob = await image.blob();
		if (state.imageUrl)
			URL.revokeObjectURL(state.imageUrl);
		state.imageUrl = URL.createObjectURL(blob);
		box.innerHTML = '';
		const img = document.createElement('img');
		img.alt = `Prévia — ${rec.name}`;
		img.src = state.imageUrl;
		box.appendChild(img);
		setMessage(container, state.dirty ? 'Alteração ainda não salva.' : '');
	}
	catch (e)
	{
		if (seq === state.previewSeq)
			setMessage(container, e?.message || String(e), true);
	}
};

/**
 * Carrega empresas e impressoras visíveis ao usuário.
 */
const loadBaseData = async () =>
{
	const [companies, devices] = await Promise.all([
		api.call(api.list_companies, '?limit=0'),
		api.call(api.list_devices, '?limit=0'),
	]);
	state.companies = (Array.isArray(companies) ? companies : [])
		.filter((c) => !c.is_disabled)
		.sort((a, b) => String(a.nick || '').localeCompare(String(b.nick || ''), 'pt-BR', { sensitivity: 'base' }));
	state.devices = (Array.isArray(devices) ? devices : [])
		.filter((d) => String(d.type || '').toUpperCase() === 'P' && !d.is_disabled);
	if (!state.companyId && state.companies.length)
		state.companyId = state.companies[0].id;
};

/**
 * Lista das etiquetas da empresa.
 * @param {HTMLElement} container
 */
const loadList = async (container) =>
{
	if (!state.companyId)
	{
		container.innerHTML = `<div class="form"><div class="title">${esc(TITLE)}</div><p class="mts">Seu usuário não tem empresa associada.</p></div>`;
		return;
	}
	const [data, logo] = await Promise.all([
		api.call(api.list_label_templates, state.companyId),
		api.call(api.select_label_logo, state.companyId),
	]);
	state.list = Array.isArray(data) ? data : [];
	state.logo = logo && logo.gfa ? logo : null;
	renderList(container);
};

/**
 * Grava a logo convertida.
 * @param {HTMLElement} container
 */
const saveLogo = async (container) =>
{
	if (!state.logoDraft)
		return notify('Escolha a imagem da logo.', TITLE);
	const logo = await api.call(api.save_label_logo, state.companyId, { gfa: state.logoDraft.gfa });
	if (!logo || !logo.gfa)
		return;
	state.logo = logo;
	renderList(container);
	notify('Logo salva. Toda etiqueta desta empresa que tiver {logo_cliente} no modelo já imprime com ela.', TITLE);
};

/**
 * Remove a logo da empresa.
 * @param {HTMLElement} container
 */
const removeLogo = (container) =>
{
	ask('Remover a logo do cliente desta empresa?', TITLE, async (answer) =>
	{
		if (answer !== 'Y')
			return;
		const logo = await api.call(api.delete_label_logo, state.companyId);
		if (logo == null)
			return;
		state.logo = logo && logo.gfa ? logo : null;
		renderList(container);
	});
};

/**
 * Abre o editor da etiqueta.
 * @param {HTMLElement} container
 * @param {string} key
 */
const openEditor = async (container, key) =>
{
	const rec = await api.call(api.select_label_template, state.companyId, key);
	if (!rec || !rec.key)
		return;
	if (rec.fragment)
	{
		const summary = state.list.find((item) => item.key === key);
		rec.preview_width_mm = summary ? summary.width_mm : rec.width_mm;
	}
	state.current = rec;
	state.savedZpl = rec.zpl || '';
	state.dirty = false;
	renderEditor(container);
};

/**
 * Pergunta antes de perder alteração não salva.
 * @param {() => void} proceed
 */
const confirmDiscard = (proceed) =>
{
	if (!state.dirty)
		return proceed();
	ask('Há alteração não salva no modelo. Deseja descartar?', TITLE, (answer) =>
	{
		if (answer === 'Y')
			proceed();
	});
};

/**
 * Imprime uma via com os dados de exemplo.
 * @param {HTMLElement} container
 */
const testPrint = async (container) =>
{
	const deviceId = Number($('#lt-device', container).value);
	if (!deviceId)
		return notify('Escolha a impressora.', TITLE);
	const useAgent = $('#lt-agent', container).checked;
	const result = await api.call(api.test_print_label_template, state.companyId, state.current.key, {
		zpl: $('#lt-zpl', container).value,
		device_id: deviceId,
		device_company_id: state.companyId,
		return_zpl: useAgent,
	});
	if (result == null)
		return;
	if (result.zpl)
	{
		try
		{
			await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
		}
		catch (e)
		{
			notify(esc(e?.message || String(e)), 'Agente local');
			return;
		}
	}
	notify('Etiqueta de teste enviada para a impressora.', TITLE);
};

/**
 * Grava o modelo da empresa.
 * @param {HTMLElement} container
 */
const save = async (container) =>
{
	const zpl = $('#lt-zpl', container).value;
	const rec = await api.call(api.save_label_template, state.companyId, state.current.key, { zpl });
	if (!rec || !rec.key)
		return;
	state.current = { ...rec, preview_width_mm: state.current.preview_width_mm };
	state.savedZpl = rec.zpl;
	state.dirty = false;
	renderEditor(container);
	notify(rec.is_custom
		? 'Modelo salvo. A próxima impressão desta etiqueta, nesta empresa, já usa o modelo novo.'
		: 'O modelo é igual ao padrão do SIAL: a empresa volta a usar o padrão.', TITLE);
};

/**
 * Apaga o modelo da empresa.
 * @param {HTMLElement} container
 */
const reset = (container) =>
{
	ask('Apagar o modelo desta empresa e voltar ao padrão do SIAL?', TITLE, async (answer) =>
	{
		if (answer !== 'Y')
			return;
		const rec = await api.call(api.reset_label_template, state.companyId, state.current.key);
		if (!rec || !rec.key)
			return;
		state.current = { ...rec, preview_width_mm: state.current.preview_width_mm };
		state.savedZpl = rec.zpl;
		state.dirty = false;
		renderEditor(container);
	});
};

/**
 * Insere `{variavel}` na posição do cursor.
 * @param {HTMLElement} container
 * @param {string} name
 */
const insertVariable = (container, name) =>
{
	const area = $('#lt-zpl', container);
	const text = `{${name}}`;
	const start = area.selectionStart ?? area.value.length;
	const end = area.selectionEnd ?? start;
	area.focus();
	area.setRangeText(text, start, end, 'end');
	state.dirty = area.value !== state.savedZpl;
	schedulePreview(container);
};

const label_templates = async function()
{
	const workarea = document.getElementById('workarea');
	if (!workarea)
		return;
	workarea.innerHTML = `<style>${styles}</style><div id="label-templates-wrap"><div id="label-templates"></div></div>`;
	const container = $('#label-templates', workarea);
	state.current = null;
	state.dirty = false;

	try
	{
		await loadBaseData();
		await loadList(container);
	}
	catch (e)
	{
		notify(esc(e?.message || String(e)), TITLE);
		return;
	}

	container.addEventListener('change', async (ev) =>
	{
		if (ev.target.id === 'lt-company')
		{
			const next = ev.target.value;
			const previous = state.companyId;
			confirmDiscard(async () =>
			{
				state.companyId = next;
				const key = state.current?.key;
				await loadList(container);
				if (key)
					await openEditor(container, key);
			});
			if (state.dirty)
				ev.target.value = previous;
			return;
		}
		if (['lt-w', 'lt-h', 'lt-dpmm'].includes(ev.target.id))
			schedulePreview(container, 0);
		if (ev.target.id === 'lt-logo-file')
		{
			const file = ev.target.files?.[0];
			if (!file)
				return;
			try
			{
				state.logoImage = await loadImageFile(file);
				refreshLogoDraft(container);
			}
			catch (e)
			{
				state.logoImage = null;
				notify(esc(e?.message || String(e)), TITLE);
			}
		}
	});

	container.addEventListener('input', (ev) =>
	{
		if (['lt-logo-w', 'lt-logo-h', 'lt-logo-cut'].includes(ev.target.id))
		{
			refreshLogoDraft(container);
			return;
		}
		if (ev.target.id !== 'lt-zpl')
			return;
		state.dirty = ev.target.value !== state.savedZpl;
		schedulePreview(container);
	});

	container.addEventListener('keydown', (ev) =>
	{
		if (ev.target.id === 'lt-zpl' && ev.key === 'Tab')
		{
			ev.preventDefault();
			ev.target.setRangeText('\t', ev.target.selectionStart, ev.target.selectionEnd, 'end');
		}
	});

	container.addEventListener('click', async (ev) =>
	{
		const row = ev.target.closest('tr[data-key]');
		if (row && !state.current)
		{
			await openEditor(container, row.dataset.key);
			return;
		}
		const varBtn = ev.target.closest('.lt-var-btn');
		if (varBtn)
		{
			insertVariable(container, varBtn.dataset.var);
			return;
		}
		switch (ev.target.id)
		{
			case 'lt-save':
				await save(container);
				break;
			case 'lt-discard':
				$('#lt-zpl', container).value = state.savedZpl;
				state.dirty = false;
				schedulePreview(container, 0);
				break;
			case 'lt-load-default':
				$('#lt-zpl', container).value = state.current.default_zpl;
				state.dirty = state.current.default_zpl !== state.savedZpl;
				schedulePreview(container, 0);
				break;
			case 'lt-reset':
				reset(container);
				break;
			case 'lt-back':
				confirmDiscard(() => { loadList(container); });
				break;
			case 'lt-refresh':
				schedulePreview(container, 0);
				break;
			case 'lt-test-print':
				await testPrint(container);
				break;
			case 'lt-help':
				openHelp();
				break;
			case 'lt-logo-save':
				await saveLogo(container);
				break;
			case 'lt-logo-remove':
				removeLogo(container);
				break;
			default:
				break;
		}
	});
};

export default label_templates;
